import { useState, useEffect, useRef } from 'react';

export default function QuoteForm() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    project_address: '',
    service_needed: '',
    project_details: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Phone validation - accept formats like 405-367-6799, (405) 367-6799, 4053676799
    const phoneRegex = /^\+?1?[-.\s]?(\(?\d{3}\)?)?[-.\s]?\d{3}[-.\s]?\d{4}$/;
    if (!phoneRegex.test(formData.phone.trim())) {
      errors.phone = 'Please enter a valid phone number';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('project_address', formData.project_address);
      formBody.append('service_needed', formData.service_needed);
      formBody.append('project_details', formData.project_details);

      const response = await fetch('https://readdy.ai/api/form/d5i6ckjihlvellpk58s0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          project_address: '',
          service_needed: '',
          project_details: ''
        });
        setFieldErrors({});
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section ref={sectionRef} id="quote" className="py-16 lg:py-24 bg-gray-900 relative overflow-hidden">
      {/* Subtle texture */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8">
        <div className={`text-center mb-8 lg:mb-12 ${isVisible ? 'lg:animate-fade-up' : 'lg:opacity-0'}`}>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4 leading-[1.05]" style={{ fontFamily: 'Oswald, sans-serif' }}>
            Get Your Free Quote Today
          </h2>
          <p className="text-xl text-gray-300 leading-[1.45]">
            Serving commercial properties across the Oklahoma City metro
          </p>
        </div>

        <form onSubmit={handleSubmit} className={`bg-white rounded-2xl p-6 lg:p-10 shadow-2xl ${isVisible ? 'lg:animate-fade-up lg:delay-80' : 'lg:opacity-0'}`} data-readdy-form id="quote-form">
          <div className="grid md:grid-cols-2 gap-4 lg:gap-6 mb-4 lg:mb-6">
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none transition-all text-sm"
                placeholder="Your name"
                aria-label="Name"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none transition-all text-sm ${
                  fieldErrors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="your@email.com"
                aria-label="Email"
                aria-invalid={!!fieldErrors.email}
              />
              {fieldErrors.email && (
                <p className="text-red-600 text-xs mt-1">{fieldErrors.email}</p>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 lg:gap-6 mb-4 lg:mb-6">
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                Phone *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none transition-all text-sm ${
                  fieldErrors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="405-367-6799"
                aria-label="Phone"
                aria-invalid={!!fieldErrors.phone}
              />
              {fieldErrors.phone && (
                <p className="text-red-600 text-xs mt-1">{fieldErrors.phone}</p>
              )}
            </div>
            <div>
              <label htmlFor="service_needed" className="block text-sm font-semibold text-gray-700 mb-2">
                Service Needed *
              </label>
              <select
                id="service_needed"
                name="service_needed"
                value={formData.service_needed}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none transition-all cursor-pointer text-sm"
                aria-label="Service Needed"
              >
                <option value="">Select a service</option>
                <option value="Parking Lot Striping">Parking Lot Striping</option>
                <option value="Asphalt Sealcoating">Asphalt Sealcoating</option>
                <option value="Crack Sealing">Crack Sealing</option>
                <option value="Pothole Repair">Pothole Repair</option>
                <option value="ADA Compliance">ADA Compliance</option>
                <option value="Maintenance Plan">Maintenance Plan</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="mb-4 lg:mb-6">
            <label htmlFor="project_address" className="block text-sm font-semibold text-gray-700 mb-2">
              Project Address *
            </label>
            <input
              type="text"
              id="project_address"
              name="project_address"
              value={formData.project_address}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none transition-all text-sm"
              placeholder="Property address in Oklahoma City metro"
              aria-label="Project Address"
            />
          </div>

          <div className="mb-4 lg:mb-6">
            <label htmlFor="project_details" className="block text-sm font-semibold text-gray-700 mb-2">
              Project Details
            </label>
            <textarea
              id="project_details"
              name="project_details"
              value={formData.project_details}
              onChange={handleChange}
              maxLength={500}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none transition-all resize-none text-sm"
              placeholder="Tell us about your project (max 500 characters)"
              aria-label="Project Details"
            ></textarea>
            <p className="text-sm text-gray-500 mt-1">{formData.project_details.length}/500 characters</p>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-4 lg:mb-6 p-4 bg-green-50 border border-green-200 rounded-lg" role="alert" aria-live="polite">
              <p className="text-green-800 font-semibold flex items-center text-sm">
                <i className="ri-checkbox-circle-line text-xl mr-2"></i>
                Thanks — we received your request. We'll call you within one business day.
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-4 lg:mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert" aria-live="polite">
              <p className="text-red-800 font-semibold flex items-center text-sm mb-3">
                <i className="ri-error-warning-line text-xl mr-2"></i>
                Something went wrong. Please try again or contact us directly:
              </p>
              <div className="flex flex-wrap gap-4 text-sm">
                <a href="tel:405-367-6799" className="text-red-700 hover:text-red-900 font-semibold underline cursor-pointer">
                  <i className="ri-phone-line mr-1"></i>
                  Call 405-367-6799
                </a>
                <a href="mailto:Contact@SoonerBlacktop.com" className="text-red-700 hover:text-red-900 font-semibold underline cursor-pointer">
                  <i className="ri-mail-line mr-1"></i>
                  Email Us
                </a>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-8 py-4 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-500 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center lg:hover-lift"
            aria-label="Request Free Quote"
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-2 text-xl"></i>
                Submitting...
              </>
            ) : (
              <>
                Request Free Quote
                <i className="ri-arrow-right-line ml-2 text-xl"></i>
              </>
            )}
          </button>

          <div className="grid md:grid-cols-3 gap-4 lg:gap-6 mt-6 lg:mt-8 pt-6 lg:pt-8 border-t border-gray-200">
            <div className="flex items-center justify-center text-gray-600">
              <i className="ri-shield-check-line text-yellow-500 text-2xl mr-2"></i>
              <span className="text-sm font-semibold">Licensed & Insured</span>
            </div>
            <div className="flex items-center justify-center text-gray-600">
              <i className="ri-file-list-3-line text-yellow-500 text-2xl mr-2"></i>
              <span className="text-sm font-semibold">Free Estimates</span>
            </div>
            <div className="flex items-center justify-center text-gray-600">
              <i className="ri-time-line text-yellow-500 text-2xl mr-2"></i>
              <span className="text-sm font-semibold">Fast Response</span>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
